/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect } from 'react';
import {
    RouterProvider, createRoutesFromElements, Route, Outlet, Navigate,
} from 'react-router-dom';
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v6';
import LayoutWithNavbar from 'components/LayoutWithNavbar/LayoutWithNavbar';
import DealDashboard from 'views/DealDashboard/DealDashboard';
import CreateDeal from 'views/Deals/CreateDeal/CreateDeal';
import ScenarioComparison from 'views/ScenarioComparison/ScenarioComparison';
import ProFormaWizard from 'views/ProFormaWizard/ProFormaWizard';
import ReportBuilder from 'views/ReportBuilder/ReportBuilder';
import { FEATURE_IDS } from 'services/constants';
import Documents from 'views/DocumentLibrary/Documents';
import { useAuth0TokenContext } from 'contexts/Auth0TokenContext';
import { NSLoadingScreen } from 'bricks';
import { AuthProvider } from 'contexts/AuthContext';
import AccountCreated from 'views/AccountCreated/AccountCreated';
import ProFormaUpload from 'views/ProFormaUpload/components/ProFormaUpload/ProFormaUpload';
import DealsBulkUpload from 'views/Deals/BulkUpload/BulkUpload';
import useGetAuthenticatedUser from 'queries/UserMenu/useGetAuthenticatedUser';
import ProFormas from 'views/ProFormas/ProFormas';
import ProFormaTable from './views/ProFormaTable/ProFormaTable';
import Reports from './views/Reports/Reports';
import InvestmentMemo from './views/InvestmentMemo/InvestmentMemo';
import NSRoute from './components/NSRoute/NSRoute';
import Deals from './views/Deals/Deals';
import { URLS } from './services/urls';
import ProFormaHome from './views/ProFormaHome/ProFormaHome';
import { ReportPageRouter } from './views/Reports/ReportPageRouter';
import { BackOfEnvelopeProvider } from './contexts/BackOfEnvelopeContext';
import { ReportPreview } from './views/Reports/ReportTypes/ReportPreview';
import AppRouterRedirectHandler from './AppRouterRedirectHandler';
import NotFound from './views/public/NotFound/NotFound';

// Needed so that navbar and deals dashboard have acces to router params (dealId)
const BackOfEnvelopeWrapper = () => (
    <BackOfEnvelopeProvider>
        <Outlet />
    </BackOfEnvelopeProvider>
);

const AuthorizedAppRouter = () => {
    const {
        isLoading, auth0Loading, isAuthenticated, login, accessToken,
    } = useAuth0TokenContext();

    const {
        data: authenticatedUser,
        isSuccess: isAuthenticatedUserSuccess,
        error: authenticatedUserError,
    } = useGetAuthenticatedUser({
        enabled: !!accessToken,
    });

    useEffect(() => {
        // if 4xx error, force new login to refresh token (handles case where role/scope changes)
        if (authenticatedUserError?.status && authenticatedUserError?.status >= 400 && authenticatedUserError?.status < 500) {
            login({
                appState: {
                    returnTo: window.location.pathname + window.location.search,
                },
            });
        }
    }, [authenticatedUserError, login]);

    const router = createBrowserRouter(
        createRoutesFromElements(
            <>
                {/* PUBLIC ROUTES */}
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="*" element={<NotFound />} />

                {/* AUTHENTICATED ROUTES */}
                <Route
                    element={(
                        <AppRouterRedirectHandler>
                            <BackOfEnvelopeProvider>
                                <LayoutWithNavbar />
                            </BackOfEnvelopeProvider>
                        </AppRouterRedirectHandler>
                    )}
                >
                    <Route path="/" element={<Navigate to={URLS.DEALS.HOME} replace />} />
                    <Route element={<NSRoute />}>
                        <Route path={URLS.ACCOUNT_CREATED} element={<AccountCreated />} handle={{ featureIds: [FEATURE_IDS.DEAL_PIPELINE] }} />
                        <Route path={URLS.REPORTS.HOME} element={<Reports />} handle={{ featureIds: [FEATURE_IDS.REPORTING_ACROSS_DEALS] }} />
                        <Route path={URLS.REPORTS.NEW} element={<ReportPageRouter />} handle={{ featureIds: [FEATURE_IDS.REPORTING_ACROSS_DEALS] }} />
                        <Route
                            path={URLS.REPORTS.REPORT_PAGE}
                            element={<ReportPageRouter />}
                            handle={{ featureIds: [FEATURE_IDS.REPORTING_ACROSS_DEALS] }}
                        />
                        <Route
                            path={URLS.REPORTS.REPORT_PREVIEW}
                            element={<ReportPreview />}
                            handle={{ featureIds: [FEATURE_IDS.REPORTING_ACROSS_DEALS] }}
                        />
                        <Route path={URLS.INVESTMENT_MEMO} element={<InvestmentMemo />} handle={{ featureIds: [FEATURE_IDS.DEAL_PIPELINE] }} />
                        <Route path={URLS.REPORT_BUILDER} element={<ReportBuilder />} handle={{ featureIds: [FEATURE_IDS.DEAL_REPORT_BUILDER] }} />
                        <Route path={URLS.DEALS.HOME} element={<Deals />} handle={{ featureIds: [FEATURE_IDS.DEAL_PIPELINE] }} />
                        <Route element={<BackOfEnvelopeWrapper />}>
                            <Route path={URLS.DEAL_DASHBOARD.HOME} element={<DealDashboard />} handle={{ featureIds: [FEATURE_IDS.DEAL_DASHBOARD] }} />
                            <Route path={URLS.DEAL_INVESTMENT_MEMO} element={<InvestmentMemo />} handle={{ featureIds: [FEATURE_IDS.DEAL_PIPELINE] }} />
                            <Route path={URLS.PRO_FORMA.HOME} element={<ProFormaHome />} handle={{ featureIds: [FEATURE_IDS.PRO_FORMA] }} />
                            <Route path={URLS.PRO_FORMA.TABLE} element={<ProFormaTable />} handle={{ featureIds: [FEATURE_IDS.PRO_FORMA] }} />
                        </Route>
                        <Route path={URLS.DEALS.COMPARISON} element={<ScenarioComparison />} handle={{ featureIds: [FEATURE_IDS.BACK_OF_ENVELOPE] }} />
                        <Route path={URLS.DEALS.FORM} element={<CreateDeal />} handle={{ featureIds: [FEATURE_IDS.DEAL_PIPELINE] }} />
                        <Route path={URLS.DEALS.BULK_UPLOAD} element={<DealsBulkUpload />} handle={{ featureIds: [FEATURE_IDS.DEAL_PIPELINE] }} />
                        <Route path={URLS.DEALS.PRO_FORMAS} element={<ProFormas />} handle={{ featureIds: [FEATURE_IDS.PRO_FORMA] }} />
                        <Route path={URLS.PRO_FORMA.HOME} element={<ProFormaHome />} handle={{ featureIds: [FEATURE_IDS.PRO_FORMA] }} />
                        <Route path={URLS.DOCUMENTS} element={<Documents />} handle={{ featureIds: [FEATURE_IDS.PRO_FORMA] }} />
                        <Route path={URLS.PRO_FORMA.TABLE} element={<ProFormaTable />} handle={{ featureIds: [FEATURE_IDS.PRO_FORMA] }} />
                        <Route path={URLS.PRO_FORMA.WIZARD} element={<ProFormaWizard />} handle={{ featureIds: [FEATURE_IDS.PRO_FORMA] }} />
                        <Route path={URLS.PRO_FORMA.UPLOAD} element={<ProFormaUpload />} handle={{ featureIds: [FEATURE_IDS.PRO_FORMA] }} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                </Route>
            </>,
        ),
    );

    if (!isLoading && !auth0Loading && !isAuthenticated) {
        login({
            appState: {
                returnTo: window.location.pathname + window.location.search,
            },
        });
    }

    if (isLoading || auth0Loading || !isAuthenticatedUserSuccess) {
        return <NSLoadingScreen />;
    }

    return (
        <AuthProvider authenticatedUser={authenticatedUser}>
            <RouterProvider router={router} />
        </AuthProvider>
    );
};

export default AuthorizedAppRouter;
